import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteCard from '@/components/feature/SiteCard';
import { navSites, siteCategories } from '@/mocks/navigation';

const sortOptions = [
  { value: 'heat', label: '按热度排序' },
  { value: 'score', label: '按评分排序' },
  { value: 'latest', label: '按更新时间' },
];

const categoryLabel = (id: string) => siteCategories.find((item) => item.id === id)?.name ?? '';

export default function DirectorySection() {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [keyword, setKeyword] = useState(searchParams.get('q') ?? '');
  const [sort, setSort] = useState('heat');
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    setKeyword(searchParams.get('q') ?? '');
  }, [searchParams]);

  useEffect(() => {
    setVisibleCount(12);
  }, [activeCategory, keyword, sort]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = navSites.filter((site) => {
      const inCategory = activeCategory === 'all' || site.category === activeCategory;
      if (!inCategory) return false;
      if (!kw) return true;
      return (
        site.name.toLowerCase().includes(kw) ||
        site.desc.toLowerCase().includes(kw) ||
        categoryLabel(site.category).toLowerCase().includes(kw) ||
        site.tags.some((tag) => tag.toLowerCase().includes(kw))
      );
    });

    const sorted = [...list];
    if (sort === 'heat') sorted.sort((a, b) => b.heat - a.heat);
    else if (sort === 'score') sorted.sort((a, b) => b.score - a.score);
    else sorted.sort((a, b) => b.updated.localeCompare(a.updated));
    return sorted;
  }, [activeCategory, keyword, sort]);

  const countOf = (id: string) =>
    id === 'all' ? navSites.length : navSites.filter((site) => site.category === id).length;

  const shown = filtered.slice(0, visibleCount);

  const resetFilters = () => {
    setActiveCategory('all');
    setKeyword('');
    setSort('heat');
  };

  return (
    <section id="directory" aria-labelledby="directory-title" className="w-full px-4 md:px-6 lg:px-10 py-14 md:py-20 bg-background-50">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="font-label text-xs tracking-[0.2em] text-primary-600">DIRECTORY</span>
          <h2 id="directory-title" className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            全部站点目录
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
            按分类筛选、按关键词检索、按热度或评分排序，找出最适合你的那个信息入口。
          </p>
        </div>
        <p className="font-label text-sm text-foreground-600">
          共 <strong className="text-primary-600">{filtered.length}</strong> 个结果
        </p>
      </div>

      <div className="mt-7 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            aria-pressed={activeCategory === 'all'}
            className={`font-label text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors ${
              activeCategory === 'all'
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300'
            }`}
          >
            全部 · {countOf('all')}
          </button>
          {siteCategories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveCategory(category.id)}
              aria-pressed={activeCategory === category.id}
              className={`font-label text-xs px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                activeCategory === category.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-700 border border-background-200 hover:border-primary-300'
              }`}
            >
              {category.short} · {countOf(category.id)}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div className="flex flex-1 items-center gap-2 h-10 rounded-md border border-background-200 bg-background-50 px-3">
            <span className="w-4 h-4 flex items-center justify-center text-sm text-foreground-400">
              <i className="ri-search-line" aria-hidden="true"></i>
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="在结果中搜索站点名称、简介或标签"
              aria-label="在结果中搜索站点"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword('')}
                aria-label="清空搜索关键词"
                className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-circle-line" aria-hidden="true"></i>
              </button>
            )}
          </div>

          <label className="flex items-center gap-2 h-10 rounded-md border border-background-200 bg-background-50 px-3">
            <span className="w-4 h-4 flex items-center justify-center text-sm text-foreground-400">
              <i className="ri-sort-desc" aria-hidden="true"></i>
            </span>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              aria-label="排序方式"
              className="bg-transparent text-sm text-foreground-900 outline-none font-label cursor-pointer"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            {shown.map((site) => (
              <SiteCard key={site.id} site={site} categoryName={categoryLabel(site.category)} />
            ))}
          </div>

          {visibleCount < filtered.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + 12)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-background-300 bg-background-50 font-label text-sm text-foreground-800 whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-colors"
              >
                加载更多站点
                <i className="ri-arrow-down-line" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
            <i className="ri-search-eye-line" aria-hidden="true"></i>
          </span>
          <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的站点</h3>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            换个关键词试试，或者先回到全部分类看看。也可以直接提交你常用但还没收录的站点。
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 font-label text-sm whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            <i className="ri-restart-line" aria-hidden="true"></i>
            重置筛选条件
          </button>
        </div>
      )}
    </section>
  );
}